import React, { useState } from "react"
import { Button, Input, message, Space, Typography, Card, Upload, Tooltip, Select, DatePicker } from "antd"
import { EditOutlined, MailOutlined, UploadOutlined } from "@ant-design/icons"
import DataTable from "../../../components/DataTable/DataTable"
import { Survey, SurveyCompletion } from "../../../../domain/entities/Survey"
import { SurveysViewModel } from "../SurveysViewModel"
import DataTableRowsSkeleton from "../../../styledComponents/LoadingSkeletons/DataTableRowsSkeleton"
import { useTranslation } from "react-i18next"
import SurveyModal from "./SurveyModal"
import dayjs from "dayjs"

const { TextArea } = Input
const { Title, Text } = Typography

interface SurveyUsersTableProps {
	survey: Survey
	viewModel: SurveysViewModel
}

interface UserCompletion {
	userId: number
	email: string
	completedAt?: string
	expired?: boolean
	completed: boolean
	comments?: string
	completionId?: number
	pdf?: string
}

const SurveyUsersTable = ({ survey, viewModel }: SurveyUsersTableProps) => {
	const { t } = useTranslation("surveys", { keyPrefix: "form.users" })
	const [tempComment, setTempComment] = useState("")
	const [savingComment, setSavingComment] = useState(false)
	const [pdfFile, setPdfFile] = useState<File | null>(null)
	const [exportingPDF, setExportingPDF] = useState<number | null>(null)
	const [sendingEmail, setSendingEmail] = useState<number | null>(null)
	const [modalVisible, setModalVisible] = useState(false)
	const [selectedUser, setSelectedUser] = useState<UserCompletion | null>(null)
	const [emailFilter, setEmailFilter] = useState("")
	const [expiredFilter, setExpiredFilter] = useState<"all" | "yes" | "no">("all")
	const [dateFilter, setDateFilter] = useState<any>(null)
	const [lastEmailSentMessage, setLastEmailSentMessage] = useState<{
		type: "success" | "error"
		message: string
	} | null>(null)

	const getSelectedUserLanguageSuffix = () => {
		if (!selectedUser) return ""
		const completion = survey.surveyCompletitions?.find(c => c.id === selectedUser.completionId)
		const language = completion?.user?.language
		if (!language) return ""
		const normalizedLanguage = language.split(/[-_]/)[0].toUpperCase()

		return normalizedLanguage ? ` (${normalizedLanguage})` : ""
	}

	const getCompletedUsers = (): UserCompletion[] => {
		if (!survey.surveyCompletitions) return []
		return survey.surveyCompletitions
			.filter((completion: SurveyCompletion) => completion.completed === true)
			.map((completion: SurveyCompletion) => ({
				userId: completion.user.id,
				email: completion.user.email,
				completedAt: completion.completedAt,
				expired: (completion as any).expired,
				completed: completion.completed,
				comments: completion.commentary || "",
				completionId: completion.id,
				pdf: completion.pdf
			}))
			.sort((a: UserCompletion, b: UserCompletion) => a.email.localeCompare(b.email))
	}

	const getPdfFileName = (pdfUrl?: string) => {
		if (!pdfUrl) return ""
		const lastPart = pdfUrl.split("/").pop()
		return lastPart ? decodeURIComponent(lastPart) : ""
	}

	const truncateText = (text: string, maxLength: number = 100) => {
		if (!text || text.length <= maxLength) return text
		return text.substring(0, maxLength) + "..."
	}

	const handleEditComment = (user: UserCompletion) => {
		setSelectedUser(user)
		setTempComment(user.comments || "")
		setPdfFile(null)
		setModalVisible(true)
		setLastEmailSentMessage(null)
	}

	const handleSaveComment = async () => {
		if (savingComment || !selectedUser) return // Evitar múltiples llamadas

		setSavingComment(true)
		try {
			if (!selectedUser.completionId) {
				message.error(t("commentSaveError"))
				return
			}

			await viewModel.updateSurveyCompletion({
				completionId: selectedUser.completionId,
				commentary: tempComment,
				pdfFile: pdfFile || undefined
			})

			message.success(t("commentSaved"))
		} catch (error) {
			message.error(t("commentSaveError"))
		} finally {
			setSavingComment(false)
		}
	}

	const handleCancelEdit = () => {
		if (savingComment) return // No permitir cancelar durante guardado
		handleCloseModal()
	}

	const handleCloseModal = () => {
		setModalVisible(false)
		setSelectedUser(null)
		setPdfFile(null)
		setLastEmailSentMessage(null)
	}

	const handleExportUserPDF = async (user: UserCompletion) => {
		if (!survey.id) {
			message.error(t("exportError"))
			return
		}

		setExportingPDF(user.userId)
		try {
			console.log("📄 Exportando PDF para usuario:", user.email, "ID:", user.userId)
			await viewModel.exportSurveyUserPDF(survey.id, user.userId, survey.title, user.email)
			message.success(t("exportSuccess"))
		} catch (error) {
			console.error("Error exportando PDF del usuario:", error)
			message.error(t("exportError"))
		} finally {
			setExportingPDF(null)
		}
	}

	const handleSendEmail = async (user: UserCompletion) => {
		if (!survey.id) {
			message.error(t("emailError"))
			return
		}

		setSendingEmail(user.userId)
		try {
			console.log("📧 Enviando email para usuario:", user.email, "ID:", user.userId)
			const result = await viewModel.sendSurveyEmail(survey.id, user.userId, survey.title, user.email)
			if (result.success) {
				message.success(result.message || t("emailSuccess"))
			} else {
				message.error(result.message || t("emailError"))
			}
		} catch (error) {
			console.error("Error enviando email del usuario:", error)
			message.error(t("emailError"))
		} finally {
			setSendingEmail(null)
		}
	}

	const handleSendEmailFromModal = async () => {
		if (!selectedUser) return
		if (!survey.id) {
			setLastEmailSentMessage({
				type: "error",
				message: t("emailError")
			})
			return
		}

		setSendingEmail(selectedUser.userId)
		try {
			console.log("📧 Enviando email para usuario:", selectedUser.email, "ID:", selectedUser.userId)
			const result = await viewModel.sendSurveyEmail(
				survey.id,
				selectedUser.userId,
				survey.title,
				selectedUser.email
			)
			if (result.success) {
				setLastEmailSentMessage({
					type: "success",
					message: `Email enviado exitosamente a ${selectedUser.email}`
				})
			} else {
				setLastEmailSentMessage({
					type: "error",
					message: result.message || t("emailError")
				})
			}
		} catch (error) {
			console.error("Error enviando email del usuario:", error)
			setLastEmailSentMessage({
				type: "error",
				message: t("emailError")
			})
		} finally {
			setSendingEmail(null)
		}
	}

	const getUserAnswers = (userEmail: string) => {
		if (!survey.answers || !survey.sections) return []

		return survey.answers
			.filter(answer => answer.email === userEmail)
			.map(answer => {
				// Encontrar la pregunta correspondiente
				const question = survey.sections
					?.flatMap(section => section.questions || [])
					.find(q => q.id === answer.questionId)

				return {
					questionText: question?.text || `Pregunta ${answer.questionId}`,
					answerText:
						answer.textAnswer || (answer.selectedOption ? answer.selectedOption.text : "Sin respuesta")
				}
			})
	}

	const columns = [
		{
			title: "ID",
			dataIndex: "completionId",
			key: "completionId",
			width: "8%"
		},
		{
			title: t("email"),
			dataIndex: "email",
			key: "email",
			sorter: true,
			width: "22%"
		},
		{
			title: t("completedAt"),
			dataIndex: "completedAt",
			key: "completedAt",
			width: "20%",
			render: (value: string | undefined) => {
				if (!value) return "-"
				const formatted = dayjs(value).isValid() ? dayjs(value).format("DD/MM/YYYY HH:mm") : value
				return formatted
			}
		},
		{
			title: t("expired"),
			dataIndex: "expired",
			key: "expired",
			width: "10%",
			render: (value: boolean | undefined) => {
				if (value === undefined) return "-"
				return value ? "Sí" : "No"
			}
		},
		{
			title: t("comments"),
			dataIndex: "comments",
			key: "comments",
			width: "40%",
			render: (comments: string) => {
				const displayText = truncateText(comments)
				return comments && comments.length > 100 ? (
					<Tooltip title={comments}>
						<span>{displayText}</span>
					</Tooltip>
				) : (
					<span>{displayText || t("noComments")}</span>
				)
			}
		},
		{
			title: t("actions"),
			key: "actions",
			width: "5%",
			align: "center" as const,
			render: (_: any, record: UserCompletion) => (
				<div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
					<Button
						type="text"
						size="small"
						icon={<EditOutlined />}
						onClick={() => handleEditComment(record)}
						title={t("editComment")}
					/>
				</div>
			)
		}
	]

	const completedUsers = getCompletedUsers()
	const filteredCompletedUsers = completedUsers.filter(user => {
		const matchesEmail = emailFilter ? user.email.toLowerCase().includes(emailFilter.toLowerCase()) : true

		const matchesExpired =
			expiredFilter === "all" ? true : expiredFilter === "yes" ? user.expired === true : user.expired === false

		const matchesDate = dateFilter
			? !!user.completedAt &&
			  dayjs(user.completedAt).isValid() &&
			  dayjs(user.completedAt).isSame(dateFilter, "day")
			: true

		return matchesEmail && matchesExpired && matchesDate
	})

	const selectedCompletion = selectedUser
		? survey.surveyCompletitions?.find(c => c.id === selectedUser.completionId)
		: null
	const attachedPdfUrl = selectedCompletion?.pdf

	return (
		<>
			<div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
				<Input
					allowClear
					placeholder={t("email")}
					style={{ width: 220 }}
					value={emailFilter}
					onChange={e => setEmailFilter(e.target.value)}
				/>
				<Select
					style={{ width: 170 }}
					value={expiredFilter}
					onChange={value => setExpiredFilter(value)}
					options={[
						{ value: "all", label: `${t("expired")}: ${t("all")}` },
						{ value: "yes", label: `${t("expired")}: ${t("yes")}` },
						{ value: "no", label: `${t("expired")}: ${t("no")}` }
					]}
				/>
				<DatePicker
					allowClear
					format="DD/MM/YYYY"
					placeholder={t("completedAt")}
					style={{ width: 170 }}
					value={dateFilter}
					onChange={date => setDateFilter(date)}
				/>
				<Button
					onClick={() => {
						setEmailFilter("")
						setExpiredFilter("all")
						setDateFilter(null)
					}}
				>
					{t("clearFilters")}
				</Button>
			</div>
			<DataTable
				tableConfig={{
					sort: viewModel.tableConfig.sort,
					pageSize: viewModel.tableConfig.pageSize
				}}
				renderCustomLoadingComponent={() => <DataTableRowsSkeleton size="lg" />}
				onChange={data => {
					data.sorter &&
						viewModel.setTableConfig({ sort: { order: data.sorter.order, field: data.sorter.field } })
				}}
				columns={columns}
				paginationConfig={{
					position: ["bottomCenter"]
				}}
				dataSource={filteredCompletedUsers.map(user => ({
					key: user.completionId ?? `${user.email}-${user.completedAt}`,
					...user
				}))}
			/>

			<SurveyModal
				title={
					<Space>
						<EditOutlined />
						<span>
							{t("editUserComment")} - {selectedUser?.email}
						</span>
					</Space>
				}
				open={modalVisible}
				onCancel={handleCloseModal}
				footer={[
					lastEmailSentMessage ? (
						<div
							key="message"
							style={{
								backgroundColor: lastEmailSentMessage.type === "success" ? "#f6ffed" : "#fff1f0",
								borderColor: lastEmailSentMessage.type === "success" ? "#b7eb8f" : "#ffccc7",
								borderWidth: "1px",
								borderStyle: "solid",
								borderRadius: "4px",
								padding: "4px 8px",
								marginBottom: "16px"
							}}
						>
							<Text
								style={{
									color: lastEmailSentMessage.type === "success" ? "#52c41a" : "#ff4d4f",
									fontSize: "12px"
								}}
							>
								{lastEmailSentMessage.message}
							</Text>
						</div>
					) : null,
					<div key="buttons" style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
						<Button onClick={handleCloseModal} disabled={savingComment}>
							{t("cancel")}
						</Button>
						<Button
							type="primary"
							onClick={handleSaveComment}
							loading={savingComment}
							disabled={savingComment}
						>
							{t("save")}
						</Button>
						<Button
							type="default"
							icon={<MailOutlined />}
							onClick={handleSendEmailFromModal}
							loading={selectedUser ? sendingEmail === selectedUser.userId : false}
							disabled={savingComment || !selectedUser}
						>
							{t("sendEmailButton")}
						</Button>
					</div>
				]}
				width={900}
				maxHeight={600}
				loading={savingComment}
			>
				{selectedUser && (
					<div>
						{/* Sección de respuestas del usuario */}
						<div style={{ marginBottom: "24px" }}>
							<Title level={5} style={{ marginBottom: "16px" }}>
								{t("userAnswers")}
							</Title>
							<div style={{ maxHeight: "300px", overflowY: "auto", paddingRight: "8px" }}>
								{getUserAnswers(selectedUser.email).length > 0 ? (
									<div style={{ width: "100%" }}>
										{getUserAnswers(selectedUser.email).map((item, index) => (
											<div
												key={index}
												style={{
													padding: "12px",
													border: "1px solid #f0f0f0",
													borderRadius: "4px",
													backgroundColor: "#fafafa",
													marginBottom: "12px"
												}}
											>
												<div style={{ marginBottom: "8px" }}>
													<Text strong>Pregunta:</Text>
													<div style={{ marginTop: "4px" }}>
														<Text>{item.questionText}</Text>
													</div>
												</div>

												<div>
													<Text strong>Respuesta:</Text>
													<div style={{ marginTop: "4px" }}>
														<Text>{item.answerText}</Text>
													</div>
												</div>
											</div>
										))}
									</div>
								) : (
									<Card>
										<Text type="secondary" style={{ textAlign: "center", display: "block" }}>
											{t("noAnswersFound")}
										</Text>
									</Card>
								)}
							</div>
						</div>

						{/* Sección de adjuntar PDF */}
						<div style={{ marginBottom: "24px" }}>
							<Title level={5} style={{ marginBottom: "16px" }}>
								Adjuntar PDF
							</Title>
							{attachedPdfUrl && !pdfFile && (
								<div style={{ marginBottom: "12px" }}>
									<Text type="secondary">
										PDF adjunto: {getPdfFileName(attachedPdfUrl) || "Archivo existente"}
									</Text>
									<Space size={8} style={{ marginLeft: "8px" }}>
										<Button type="link" href={attachedPdfUrl} target="_blank" rel="noreferrer">
											Ver
										</Button>
										<Button type="link" href={attachedPdfUrl} download>
											Descargar
										</Button>
									</Space>
								</div>
							)}
							<Upload
								accept=".pdf,application/pdf"
								maxCount={1}
								fileList={pdfFile ? [{ uid: "-1", name: pdfFile.name, status: "done" }] : []}
								beforeUpload={file => {
									if (file.type !== "application/pdf") {
										message.error("Solo se permiten archivos PDF")
										return Upload.LIST_IGNORE
									}
									setPdfFile(file as File)
									return false
								}}
								onRemove={() => setPdfFile(null)}
								disabled={savingComment}
							>
								<Button icon={<UploadOutlined />} disabled={savingComment}>
									Seleccionar PDF
								</Button>
							</Upload>
						</div>

						{/* Sección de comentario editable */}
						<div>
							<Title level={5} style={{ marginBottom: "16px" }}>
								{t("adminComment")}
								{getSelectedUserLanguageSuffix()}
							</Title>
							<div
								style={{
									padding: "12px",
									border: "1px solid #f0f0f0",
									borderRadius: "4px",
									backgroundColor: "#fafafa"
								}}
							>
								<TextArea
									rows={4}
									value={tempComment}
									onChange={e => setTempComment(e.target.value)}
									placeholder={t("commentPlaceholder")}
									disabled={savingComment}
									style={{
										resize: "vertical",
										backgroundColor: "white",
										border: "1px solid #d9d9d9"
									}}
								/>
							</div>
						</div>
					</div>
				)}
			</SurveyModal>
		</>
	)
}

export default SurveyUsersTable
